import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MapPin, Phone, AlertTriangle, ChevronDown, ChevronUp, Send, ArrowLeft, ExternalLink, X, Check, Clock, Mail, Camera, Upload, Loader2 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const B = {
  navy: "#2D3561", orange: "#f64e0e", blue: "#686af3", lilac: "#a88efb",
  olive: "#8B9A2D", pink: "#e8637a", cream: "#f8f7f4", red: "#dc2626",
  heading: DISPLAY,
  mono: MONO,
};

const ANIMAL_TYPES = [
  { value: "ave", label: "Ave / Pájaro", icon: "" },
  { value: "rapaz", label: "Rapaz", icon: "" },
  { value: "mamifero", label: "Mamífero", icon: "" },
  { value: "reptil", label: "Reptil", icon: "" },
  { value: "anfibio", label: "Anfibio", icon: "" },
  { value: "marino", label: "Marino", icon: "" },
  { value: "murcielago", label: "Murciélago", icon: "" },
  { value: "otro", label: "Otro", icon: "" },
];

const URGENCY = [
  { value: "critica", label: "CRÍTICA", color: B.red },
  { value: "alta", label: "ALTA", color: B.orange },
  { value: "media", label: "MEDIA", color: B.olive },
  { value: "baja", label: "BAJA", color: B.blue },
];

const CONDITIONS = [
  { value: "herido", label: "Herido" },
  { value: "atrapado", label: "Atrapado" },
  { value: "atropellado", label: "Atropellado" },
  { value: "envenenado", label: "Envenenado" },
  { value: "cria_abandonada", label: "Cría abandonada" },
  { value: "desorientado", label: "Desorientado" },
  { value: "electrocutado", label: "Electrocutado" },
  { value: "otro", label: "Otro" },
];

/* ── Brutalist Primitives ─────────────────────────────── */

function Card({ children, className = "", bg = "#fff", accent = B.navy }) {
  return (
    <div className={className} style={{ background: bg, border: `3px solid ${accent}`, boxShadow: `6px 6px 0 ${accent}` }}>
      {children}
    </div>
  );
}

function Btn({ children, onClick, bg = B.orange, color = "#fff", className = "", disabled, type = "button" }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      className={`px-5 py-2.5 font-bold text-xs uppercase tracking-wider transition-transform ${disabled ? "opacity-40" : "hover:-translate-y-0.5 active:translate-x-[3px] active:translate-y-[3px]"} ${className}`}
      style={{ fontFamily: B.mono, background: bg, color, border: `3px solid ${B.navy}`, boxShadow: disabled ? "none" : `4px 4px 0 ${B.navy}`, cursor: disabled ? "not-allowed" : "pointer" }}>
      {children}
    </button>
  );
}

function Tag({ children, bg = B.navy, color = "#fff" }) {
  return <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase" style={{ fontFamily: B.mono, background: bg, color }}>{children}</span>;
}

function SectionTitle({ children, color = B.cream }) {
  return <h2 className="text-lg md:text-xl font-black uppercase tracking-tight" style={{ fontFamily: B.heading, color }}>{children}</h2>;
}

/* ── Protocol ──────────────────────────────────────────── */

function Protocol({ protocol }) {
  const [showDo, setShowDo] = useState(true);
  const [showDont, setShowDont] = useState(true);
  const [showSpecial, setShowSpecial] = useState(false);
  if (!protocol) return null;

  return (
    <div className="space-y-5">
      {/* DO */}
      <Card bg={B.cream} accent={B.navy} className="overflow-hidden">
        <button onClick={() => setShowDo(!showDo)} className="w-full flex items-center justify-between p-4" data-testid="protocol-do-toggle">
          <span className="font-black text-base uppercase flex items-center gap-2" style={{ fontFamily: B.heading, color: B.navy }}>
            <span style={{ color: B.blue }}>///</span> QUÉ HACER
          </span>
          {showDo ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        {showDo && (
          <div className="px-4 pb-4 space-y-1.5">
            {protocol.do.map((item, i) => (
              <div key={i} className="flex items-start gap-3 text-sm py-1" style={{ fontFamily: B.mono, borderBottom: i < protocol.do.length - 1 ? `1px solid ${B.navy}22` : "none" }}>
                <span className="font-black text-base shrink-0 w-6 text-right" style={{ color: B.blue }}>{i + 1}</span>
                <span style={{ color: B.navy }}>{item}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* DON'T */}
      <Card bg={B.navy} accent={B.orange} className="overflow-hidden">
        <button onClick={() => setShowDont(!showDont)} className="w-full flex items-center justify-between p-4" data-testid="protocol-dont-toggle">
          <span className="font-black text-base uppercase flex items-center gap-2" style={{ fontFamily: B.heading, color: B.orange }}>
            <X size={18} strokeWidth={3} /> QUÉ NO HACER
          </span>
          {showDont ? <ChevronUp size={18} color={B.cream} /> : <ChevronDown size={18} color={B.cream} />}
        </button>
        {showDont && (
          <div className="px-4 pb-4 space-y-1.5">
            {protocol.dont.map((item, i) => (
              <div key={i} className="flex items-start gap-3 text-sm py-1" style={{ fontFamily: B.mono, borderBottom: i < protocol.dont.length - 1 ? `1px solid #ffffff15` : "none" }}>
                <span className="font-black shrink-0" style={{ color: B.orange }}>X</span>
                <span style={{ color: B.cream }}>{item}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* SPECIAL CASES */}
      {protocol.special_cases && (
        <Card bg={B.cream} accent={B.olive} className="overflow-hidden">
          <button onClick={() => setShowSpecial(!showSpecial)} className="w-full flex items-center justify-between p-4" data-testid="protocol-special-toggle">
            <span className="font-black text-base uppercase flex items-center gap-2" style={{ fontFamily: B.heading, color: B.olive }}>
              <AlertTriangle size={18} strokeWidth={3} /> CASOS ESPECIALES
            </span>
            {showSpecial ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {showSpecial && (
            <div className="px-4 pb-4 space-y-3">
              {protocol.special_cases.map((sc, i) => (
                <div key={i} style={{ borderLeft: `4px solid ${B.orange}`, paddingLeft: "12px" }}>
                  <p className="font-black text-sm uppercase" style={{ fontFamily: B.heading, color: B.navy }}>{sc.case}</p>
                  <p className="text-xs mt-0.5" style={{ fontFamily: B.mono, color: B.navy }}>{sc.action}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

/* ── Center Card ───────────────────────────────────────── */

function CenterCard({ center, isNearest }) {
  return (
    <Card bg={isNearest ? B.cream : "#fff"} accent={isNearest ? B.orange : B.navy} className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {isNearest && <Tag bg={B.orange}>MÁS CERCANO</Tag>}
          <h3 className="font-black text-sm mt-1 uppercase" style={{ fontFamily: B.heading, color: B.navy }}>{center.name}</h3>
          <p className="text-[11px] mt-1 truncate" style={{ fontFamily: B.mono, color: B.navy + "99" }}>{center.description}</p>
          {center.address && <p className="text-[11px] mt-1 flex items-center gap-1" style={{ fontFamily: B.mono }}><MapPin size={11} /> {center.address}</p>}
          {center.hours && <p className="text-[11px] mt-0.5" style={{ fontFamily: B.mono, color: B.navy + "77" }}><Clock size={11} className="inline" /> {center.hours}</p>}
        </div>
        {center.distance_km !== undefined && (
          <div className="text-right shrink-0 p-2" style={{ background: B.navy, color: B.cream }}>
            <span className="font-black text-xl block" style={{ fontFamily: B.mono }}>{center.distance_km}</span>
            <span className="text-[10px] uppercase block" style={{ fontFamily: B.mono }}>km</span>
          </div>
        )}
      </div>
      <div className="flex gap-2 mt-3 flex-wrap">
        {center.phone && (
          <a href={`tel:${center.phone.replace(/\s/g, '')}`} data-testid={`call-center-${center.center_id}`}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white"
            style={{ fontFamily: B.mono, background: B.orange, border: `2px solid ${B.navy}`, boxShadow: `3px 3px 0 ${B.navy}` }}>
            <Phone size={13} /> {center.phone}
          </a>
        )}
        {center.email && (
          <a href={`mailto:${center.email}`} className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold"
            style={{ fontFamily: B.mono, background: B.lilac, color: "#fff", border: `2px solid ${B.navy}`, boxShadow: `3px 3px 0 ${B.navy}` }}>
            <Mail size={13} /> EMAIL
          </a>
        )}
        {center.website && (
          <a href={center.website} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold"
            style={{ fontFamily: B.mono, background: B.cream, color: B.navy, border: `2px solid ${B.navy}`, boxShadow: `3px 3px 0 ${B.navy}` }}>
            <ExternalLink size={13} /> WEB
          </a>
        )}
      </div>
    </Card>
  );
}

/* ── Report Form ───────────────────────────────────────── */

function ReportForm({ country, onSubmit, nearestCenter }) {
  const [form, setForm] = useState({
    animal_type: "", animal_species: "", description: "", condition: "herido",
    urgency: "media", reporter_name: "", reporter_phone: "", photo_url: "",
    location_name: "", location_lat: null, location_lng: null,
  });
  const [gettingLoc, setGettingLoc] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFileUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { alert("Foto demasiado grande (máx 10MB)"); return; }
    setUploading(true);
    setPhotoPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd);
      setForm((f) => ({ ...f, photo_url: res.data.url }));
    } catch { alert("Error al subir la foto"); setPhotoPreview(null); }
    finally { setUploading(false); }
  };

  const getLoc = useCallback(() => {
    if (!navigator.geolocation) return;
    setGettingLoc(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setForm((f) => ({ ...f, location_lat: p.coords.latitude, location_lng: p.coords.longitude })); setGettingLoc(false); },
      () => setGettingLoc(false), { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.animal_type || !form.description) return;
    setSubmitting(true);
    try { await onSubmit({ ...form, country }); } finally { setSubmitting(false); }
  };

  const Field = ({ label, children }) => (
    <div><label className="block text-[10px] font-bold mb-1.5 uppercase tracking-wider" style={{ fontFamily: B.mono, color: B.navy }}>{label}</label>{children}</div>
  );

  const inputStyle = { fontFamily: B.mono, border: `2px solid ${B.navy}`, fontSize: "13px" };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="wildlife-report-form">
      <Field label="Tipo de animal *">
        <div className="grid grid-cols-4 gap-1.5">
          {ANIMAL_TYPES.map((t) => (
            <button key={t.value} type="button" onClick={() => setForm((f) => ({ ...f, animal_type: t.value }))}
              className="p-2 text-center text-[11px] font-bold" data-testid={`animal-type-${t.value}`}
              style={{ fontFamily: B.mono, border: `2px solid ${B.navy}`,
                background: form.animal_type === t.value ? B.blue : "#fff",
                color: form.animal_type === t.value ? "#fff" : B.navy,
                boxShadow: form.animal_type === t.value ? `3px 3px 0 ${B.navy}` : "none" }}>
              <span className="text-base block">{t.icon}</span>{t.label}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Especie (si la conoces)">
        <input type="text" value={form.animal_species} onChange={(e) => setForm((f) => ({ ...f, animal_species: e.target.value }))}
          placeholder="Ej: Búho real, Ciervo..." className="w-full p-2" style={inputStyle} data-testid="animal-species-input" />
      </Field>

      <Field label="Estado del animal *">
        <div className="grid grid-cols-2 gap-1.5">
          {CONDITIONS.map((c) => (
            <button key={c.value} type="button" onClick={() => setForm((f) => ({ ...f, condition: c.value }))}
              className="p-2 text-[11px] text-left font-bold" data-testid={`condition-${c.value}`}
              style={{ fontFamily: B.mono, border: `2px solid ${B.navy}`,
                background: form.condition === c.value ? B.navy : "#fff",
                color: form.condition === c.value ? B.cream : B.navy }}>
              {c.label}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Urgencia">
        <div className="grid grid-cols-4 gap-1.5">
          {URGENCY.map((u) => (
            <button key={u.value} type="button" onClick={() => setForm((f) => ({ ...f, urgency: u.value }))}
              className="p-2 text-center text-[10px] font-black uppercase" data-testid={`urgency-${u.value}`}
              style={{ fontFamily: B.mono, border: `2px solid ${B.navy}`,
                background: form.urgency === u.value ? u.color : "#fff",
                color: form.urgency === u.value ? "#fff" : u.color }}>
              {u.label}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Descripción *">
        <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          placeholder="Tamaño, colores, comportamiento, heridas visibles..."
          rows={3} className="w-full p-2 resize-none" style={inputStyle} data-testid="description-input" />
      </Field>

      <Field label="Ubicación">
        <div className="flex gap-2">
          <input type="text" value={form.location_name} onChange={(e) => setForm((f) => ({ ...f, location_name: e.target.value }))}
            placeholder="Calle, parque, carretera..." className="flex-1 p-2" style={inputStyle} data-testid="location-name-input" />
          <Btn bg={B.blue} onClick={getLoc} disabled={gettingLoc}>
            <MapPin size={14} className="inline" /> {gettingLoc ? "..." : "GPS"}
          </Btn>
        </div>
        {form.location_lat && (
          <p className="text-[11px] mt-1 font-bold" style={{ fontFamily: B.mono, color: B.blue }}>
            <Check size={11} className="inline" /> {form.location_lat.toFixed(4)}, {form.location_lng.toFixed(4)}
          </p>
        )}
      </Field>

      <Field label="Foto del animal">
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden"
          onChange={(e) => handleFileUpload(e.target.files[0])} data-testid="camera-input" />
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => handleFileUpload(e.target.files[0])} data-testid="file-input" />
        
        {photoPreview ? (
          <div className="relative">
            <img src={photoPreview} alt="Preview" className="w-full max-h-48 object-cover" style={{ border: `2px solid ${B.navy}` }} />
            {uploading && (
              <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(41,41,77,0.7)" }}>
                <Loader2 size={24} className="animate-spin" color="#fff" />
                <span className="text-xs font-bold ml-2 text-white" style={{ fontFamily: B.mono }}>SUBIENDO...</span>
              </div>
            )}
            {!uploading && form.photo_url && (
              <div className="absolute top-1 right-1">
                <span className="px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: B.blue, fontFamily: B.mono }}>
                  <Check size={10} className="inline" /> SUBIDA
                </span>
              </div>
            )}
            <button type="button" onClick={() => { setPhotoPreview(null); setForm((f) => ({ ...f, photo_url: "" })); }}
              className="absolute top-1 left-1 p-1" style={{ background: B.orange, border: `2px solid ${B.navy}` }}>
              <X size={14} color="#fff" />
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Btn bg={B.orange} onClick={() => cameraInputRef.current?.click()} className="flex-1 text-center py-3" data-testid="camera-btn">
              <Camera size={16} className="inline mr-1" /> HACER FOTO
            </Btn>
            <Btn bg={B.blue} onClick={() => fileInputRef.current?.click()} className="flex-1 text-center py-3" data-testid="gallery-btn">
              <Upload size={16} className="inline mr-1" /> GALERÍA
            </Btn>
          </div>
        )}
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Tu nombre">
          <input type="text" value={form.reporter_name} onChange={(e) => setForm((f) => ({ ...f, reporter_name: e.target.value }))}
            placeholder="Opcional" className="w-full p-2" style={inputStyle} data-testid="reporter-name-input" />
        </Field>
        <Field label="Tu teléfono">
          <input type="tel" value={form.reporter_phone} onChange={(e) => setForm((f) => ({ ...f, reporter_phone: e.target.value }))}
            placeholder="Por si contactan" className="w-full p-2" style={inputStyle} data-testid="reporter-phone-input" />
        </Field>
      </div>

      {nearestCenter && (
        <Card bg={B.cream} accent={B.orange} className="p-3">
          <p className="text-[10px] font-bold uppercase" style={{ fontFamily: B.mono, color: B.orange }}>
            <Phone size={11} className="inline" /> Centro más cercano
          </p>
          <p className="font-black text-sm" style={{ fontFamily: B.heading, color: B.navy }}>{nearestCenter.name}</p>
          <p className="text-[11px]" style={{ fontFamily: B.mono }}>{nearestCenter.phone} · {nearestCenter.distance_km}km</p>
        </Card>
      )}

      <Btn type="submit" bg={B.orange} disabled={submitting || !form.animal_type || !form.description} className="w-full text-center py-3.5">
        <Send size={14} className="inline mr-2" /> {submitting ? "ENVIANDO..." : "ENVIAR REPORTE"}
      </Btn>
    </form>
  );
}

/* ── Report Item ───────────────────────────────────────── */

function ReportItem({ report }) {
  const animal = ANIMAL_TYPES.find((t) => t.value === report.animal_type);
  const urg = URGENCY.find((u) => u.value === report.urgency);
  const statusMap = { pendiente: B.olive, atendido: B.blue, resuelto: B.navy, descartado: "#9ca3af" };

  return (
    <Card bg="#fff" accent={B.navy} className="p-4" data-testid={`report-${report.report_id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl">{animal?.icon || ""}</span>
          <span className="font-black text-sm uppercase" style={{ fontFamily: B.heading }}>{animal?.label || report.animal_type}</span>
          {report.animal_species && <span className="text-[11px]" style={{ fontFamily: B.mono, color: B.navy + "77" }}>({report.animal_species})</span>}
        </div>
        <div className="flex gap-1">
          {urg && <Tag bg={urg.color}>{urg.label}</Tag>}
          <Tag bg={statusMap[report.status] || "#9ca3af"}>{report.status}</Tag>
        </div>
      </div>
      <p className="text-xs mt-2" style={{ fontFamily: B.mono, color: B.navy }}>{report.description}</p>
      {report.photo_url && (
        <img src={report.photo_url.startsWith("/api") ? process.env.REACT_APP_BACKEND_URL + report.photo_url : report.photo_url}
          alt="Foto del animal" className="w-full max-h-40 object-cover mt-2" style={{ border: `2px solid ${B.navy}` }} />
      )}
      {report.location_name && <p className="text-[11px] mt-1" style={{ fontFamily: B.mono, color: B.navy + "77" }}><MapPin size={11} className="inline" /> {report.location_name}</p>}
      {report.nearest_center && (
        <div className="mt-2 p-2 text-[11px] flex items-center justify-between" style={{ fontFamily: B.mono, background: B.cream, border: `2px solid ${B.orange}` }}>
          <div>
            <span className="font-bold" style={{ color: B.orange }}>Centro:</span> {report.nearest_center.name} ({report.nearest_center.distance_km}km)
          </div>
          {report.nearest_center.phone && (
            <a href={`tel:${report.nearest_center.phone.replace(/\s/g, '')}`} className="font-bold underline" style={{ color: B.orange }}>{report.nearest_center.phone}</a>
          )}
        </div>
      )}
      {report.email_sent && (
        <div className="mt-1 text-[10px] flex items-center gap-1" style={{ fontFamily: B.mono, color: B.blue }}>
          <Mail size={10} /> Email enviado al centro
        </div>
      )}
      <p className="text-[10px] mt-2" style={{ fontFamily: B.mono, color: B.navy + "55" }}>
        {new Date(report.created_at).toLocaleString("es-ES")} {report.reporter_name && `· ${report.reporter_name}`}
      </p>
    </Card>
  );
}

/* ── Main Page ─────────────────────────────────────────── */

export default function WildlifeReportPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("protocol");
  const [country, setCountry] = useState(() => localStorage.getItem("emergencia_country")?.toUpperCase() || "ES");
  const [protocol, setProtocol] = useState(null);
  const [centers, setCenters] = useState([]);
  const [nearestCenter, setNearestCenter] = useState(null);
  const [reports, setReports] = useState([]);
  const [userLoc, setUserLoc] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedReport, setSubmittedReport] = useState(null);

  useEffect(() => {
    axios.get(`${API}/wildlife/protocols/${country}`).then((r) => setProtocol(r.data)).catch(() => {});
    axios.get(`${API}/wildlife/reports?country=${country}&limit=20`).then((r) => setReports(r.data.reports)).catch(() => {});
  }, [country]);

  useEffect(() => {
    if (!navigator.geolocation) {
      axios.get(`${API}/wildlife/centers?country=${country}`).then((r) => setCenters(r.data.centers)).catch(() => {});
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLoc(loc);
        axios.get(`${API}/wildlife/centers?country=${country}&lat=${loc.lat}&lng=${loc.lng}`).then((r) => {
          setCenters(r.data.centers);
          if (r.data.centers.length > 0) setNearestCenter(r.data.centers[0]);
        }).catch(() => {});
      },
      () => { axios.get(`${API}/wildlife/centers?country=${country}`).then((r) => setCenters(r.data.centers)).catch(() => {}); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [country]);

  const handleSubmit = async (formData) => {
    try {
      const token = localStorage.getItem("ea_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/wildlife/reports`, formData, { headers });
      setSubmittedReport(res.data);
      setSubmitted(true);
      setReports((prev) => [res.data, ...prev]);
    } catch { alert("Error al enviar. Inténtalo de nuevo."); }
  };

  const TABS = [
    { id: "protocol", label: "PROTOCOLO" },
    { id: "report", label: "REPORTAR" },
    { id: "centers", label: "CENTROS" },
    { id: "reports", label: "REPORTES" },
  ];

  return (
    <div className="min-h-screen" style={{ background: B.cream }}>
      <SEO
        title="Fauna Silvestre"
        description="Reporta fauna silvestre en peligro. Localiza centros de rescate y recuperación de animales salvajes cerca de tu ubicación. España y Ecuador."
        path="/fauna"
      />

      {/* ── HEADER ─────────────────────────────────── */}
      <div style={{ background: B.navy, borderBottom: `4px solid ${B.orange}` }}>
        <div className="max-w-5xl mx-auto px-4 py-5">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => navigate(-1)} style={{ color: B.cream }} className="hover:opacity-70"><ArrowLeft size={22} /></button>
            <div>
              <h1 className="text-2xl md:text-3xl font-black uppercase tracking-tight" style={{ fontFamily: B.heading, color: B.cream }}>
                FAUNA EN PELIGRO
              </h1>
              <p className="text-xs uppercase tracking-wider" style={{ fontFamily: B.mono, color: B.lilac }}>
                Reporta · Protege · Actúa
              </p>
            </div>
          </div>

          {/* Country + Emergency */}
          <div className="flex items-center gap-3 flex-wrap mb-4">
            {["ES", "EC"].map((c) => (
              <button key={c} onClick={() => setCountry(c)}
                className="px-3 py-1 text-[11px] font-bold uppercase" data-testid={`country-toggle-${c}`}
                style={{ fontFamily: B.mono, border: `2px solid ${country === c ? B.orange : "#ffffff99"}`,
                  background: country === c ? B.orange : "rgba(255,255,255,0.1)",
                  color: "#fff" }}>
                {c === "ES" ? "ESPAÑA" : "ECUADOR"}
              </button>
            ))}
            {protocol && (
              <div className="flex items-center gap-2 ml-auto flex-wrap">
                <a href={`tel:${protocol.emergency_phone}`} data-testid="emergency-call-btn"
                  className="flex items-center gap-1.5 px-4 py-1.5 font-black text-sm"
                  style={{ fontFamily: B.mono, background: B.red, color: "#fff", border: `2px solid ${B.cream}` }}>
                  <Phone size={15} /> {protocol.emergency_phone}
                </a>
                {protocol.secondary_phones?.map((sp) => (
                  <a key={sp.number} href={`tel:${sp.number.replace(/\s/g, '')}`}
                    className="flex items-center gap-1 px-3 py-1 text-[10px] font-bold"
                    style={{ fontFamily: B.mono, background: B.orange, color: "#fff", border: `2px solid ${B.cream}` }}>
                    <Phone size={11} /> {sp.label}: {sp.number}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* TABS */}
          <div className="flex gap-0.5">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                className="px-4 py-2 text-[11px] font-bold uppercase tracking-wider"
                style={{ fontFamily: B.mono, borderBottom: "none",
                  background: tab === t.id ? B.cream : "transparent",
                  color: tab === t.id ? B.navy : B.cream + "66",
                  border: tab === t.id ? `2px solid ${B.navy}` : "2px solid transparent",
                  borderBottom: tab === t.id ? `2px solid ${B.cream}` : "none",
                  marginBottom: "-2px", position: "relative", zIndex: tab === t.id ? 1 : 0 }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── CONTENT ────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 py-6">

        {/* PROTOCOL TAB */}
        {tab === "protocol" && protocol && (
          <div className="space-y-6">
            <div className="text-center mb-2">
              <SectionTitle color={B.navy}>{protocol.title}</SectionTitle>
              <p className="text-xs mt-1" style={{ fontFamily: B.mono, color: B.navy + "88" }}>
                Sigue estos pasos si encuentras un animal silvestre herido o en peligro
              </p>
              {protocol.source && (
                <p className="text-[10px] mt-1" style={{ fontFamily: B.mono, color: B.blue }}>
                  Fuente: {protocol.source_url ? (
                    <a href={protocol.source_url} target="_blank" rel="noopener noreferrer" className="underline">{protocol.source}</a>
                  ) : protocol.source}
                </p>
              )}
            </div>

            {/* Nearest Center Banner */}
            {nearestCenter && (
              <Card bg={B.navy} accent={B.orange} className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <Tag bg={B.orange}>CENTRO MÁS CERCANO</Tag>
                    <p className="font-black text-lg mt-1" style={{ fontFamily: B.heading, color: B.cream }}>{nearestCenter.name}</p>
                    <p className="text-[11px]" style={{ fontFamily: B.mono, color: B.lilac }}>{nearestCenter.distance_km}km de tu ubicación</p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <a href={`tel:${nearestCenter.phone?.replace(/\s/g, '')}`} data-testid="call-nearest-center"
                      className="flex items-center gap-2 px-5 py-2.5 font-black text-sm"
                      style={{ fontFamily: B.mono, background: B.orange, color: "#fff", border: `3px solid ${B.cream}`, boxShadow: `4px 4px 0 ${B.cream}` }}>
                      <Phone size={16} /> {nearestCenter.phone}
                    </a>
                    <p className="text-[10px] text-center" style={{ fontFamily: B.mono, color: B.lilac }}>
                      RECOMENDAMOS LLAMAR SIEMPRE
                    </p>
                  </div>
                </div>
              </Card>
            )}

            <Protocol protocol={protocol} />
          </div>
        )}

        {/* REPORT TAB */}
        {tab === "report" && (
          <div>
            {submitted && submittedReport ? (
              <Card bg={B.navy} accent={B.orange} className="p-6 text-center">
                <Check size={40} color={B.cream} className="mx-auto mb-3" />
                <h3 className="text-xl font-black uppercase" style={{ fontFamily: B.heading, color: B.cream }}>REPORTE ENVIADO</h3>
                <p className="text-xs mt-2" style={{ fontFamily: B.mono, color: B.lilac }}>
                  Se ha notificado al equipo y a voluntarios cercanos.
                </p>
                {submittedReport.email_sent && (
                  <div className="mt-3 p-2 inline-block" style={{ background: B.blue, border: `2px solid ${B.cream}` }}>
                    <p className="text-[11px] font-bold" style={{ fontFamily: B.mono, color: "#fff" }}>
                      <Mail size={12} className="inline" /> Email enviado al centro de fauna
                    </p>
                  </div>
                )}
                {submittedReport.nearest_center && (
                  <div className="mt-4">
                    <div className="inline-block p-3" style={{ background: B.cream, border: `3px solid ${B.orange}`, boxShadow: `4px 4px 0 ${B.orange}` }}>
                      <p className="text-[10px] font-bold uppercase" style={{ fontFamily: B.mono, color: B.orange }}>CONTACTA AL CENTRO:</p>
                      <p className="font-black text-base" style={{ fontFamily: B.heading, color: B.navy }}>{submittedReport.nearest_center.name}</p>
                      <a href={`tel:${submittedReport.nearest_center.phone?.replace(/\s/g, '')}`}
                        className="font-black text-lg underline block mt-1" style={{ fontFamily: B.mono, color: B.orange }}>
                        <Phone size={14} className="inline" /> {submittedReport.nearest_center.phone}
                      </a>
                      <p className="text-[10px] mt-1" style={{ fontFamily: B.mono, color: B.navy + "77" }}>{submittedReport.nearest_center.distance_km}km</p>
                    </div>
                    <p className="text-[11px] mt-3 font-bold" style={{ fontFamily: B.mono, color: B.orange }}>
                      <Phone size={12} className="inline" /> RECOMENDAMOS LLAMAR PARA CONFIRMAR LA RECEPCIÓN
                    </p>
                  </div>
                )}
                <div className="mt-5 flex gap-2 justify-center">
                  <Btn bg={B.blue} onClick={() => { setSubmitted(false); setSubmittedReport(null); }}>NUEVO REPORTE</Btn>
                  <Btn bg={B.cream} color={B.navy} onClick={() => setTab("reports")}>VER REPORTES</Btn>
                </div>
              </Card>
            ) : (
              <div>
                <SectionTitle color={B.navy}>REPORTAR FAUNA EN PELIGRO</SectionTitle>
                <p className="text-xs mt-1 mb-5" style={{ fontFamily: B.mono, color: B.navy + "88" }}>
                  Rellena el formulario. Si la urgencia es alta, enviaremos email al centro de fauna más cercano.
                </p>
                <ReportForm country={country} onSubmit={handleSubmit} nearestCenter={nearestCenter} />
              </div>
            )}
          </div>
        )}

        {/* CENTERS TAB */}
        {tab === "centers" && (
          <div className="space-y-4">
            <SectionTitle color={B.navy}>CENTROS DE FAUNA · {country === "EC" ? "ECUADOR" : "ESPAÑA"}</SectionTitle>
            <p className="text-xs" style={{ fontFamily: B.mono, color: B.navy + "88" }}>
              {userLoc ? "Ordenados por proximidad" : "Activa geolocalización para ver distancias"}
            </p>
            {centers.map((c, i) => <CenterCard key={c.center_id} center={c} isNearest={i === 0 && !!userLoc} />)}
            {centers.length === 0 && (
              <Card bg="#fff" accent={B.navy} className="p-8 text-center">
                <p style={{ fontFamily: B.mono, color: B.navy + "77" }}>No hay centros para este país</p>
              </Card>
            )}
          </div>
        )}

        {/* REPORTS TAB */}
        {tab === "reports" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <SectionTitle color={B.navy}>REPORTES ACTIVOS</SectionTitle>
              <Btn bg={B.orange} onClick={() => setTab("report")} className="text-[10px]">+ REPORTAR</Btn>
            </div>
            {reports.length === 0 ? (
              <Card bg="#fff" accent={B.navy} className="p-8 text-center">
                <p className="font-bold" style={{ fontFamily: B.mono, color: B.navy + "77" }}>Sin reportes en {country === "EC" ? "Ecuador" : "España"}</p>
                <Btn bg={B.orange} onClick={() => setTab("report")} className="mt-4">SER EL PRIMERO</Btn>
              </Card>
            ) : (
              reports.map((r) => <ReportItem key={r.report_id} report={r} />)
            )}
          </div>
        )}
      </div>

    </div>
  );
}
